import React from 'react'

import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin'
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import classNames from 'classnames'
import { EditorState, LexicalEditor } from 'lexical'
import { BeautifulMentionsItem, BeautifulMentionsPlugin } from 'lexical-beautiful-mentions'
import dynamic from 'next/dynamic'

import { MentionsMenu, MentionsMenuItem } from './Mentions'
import ToolbarPlugin from './ToolbarPlugin'
import { initialConfig } from './initialConfig'

type MaxEditor = {
  customInitialConfig?: Partial<InitialConfigType>
  placeholder?: string
  onChange: (editorState: EditorState, editor: LexicalEditor, tags?: Set<string>) => void
  editorRef?: React.MutableRefObject<LexicalEditor | null>
  debugMode?: boolean
  textSize?: 'sm' | 'md' | 'lg'
  mentions?: {
    items: Record<string, BeautifulMentionsItem[]>
  }
}

/**
 * Max Editor is a rich text editor that uses the Lexical Framework by Meta.
 * @see https://lexical.dev
 * */
export function MaxEditor({
  customInitialConfig,
  placeholder = 'Enter some text...',
  onChange = () => null,
  editorRef,
  debugMode = false,
  textSize = 'md',
  mentions,
}: MaxEditor) {
  const TreeViewPlugin = dynamic(() => import('./TreeViewPlugin'))

  const textSizes = {
    'text-sm': textSize === 'sm',
    'text-base': textSize === 'md',
    'text-lg': textSize === 'lg',
  }

  const placeholderclassNames = classNames({
    'absolute top-3 left-3 text-gray-500 pointer-events-none': true,
    ...textSizes,
  })

  const Placeholder = () => <div className={placeholderclassNames}>{placeholder}</div>

  const contentEditableClassnames = classNames({
    'relative resize-none min-h-16 p-3 outline-none text-gray-700': true,
    ...textSizes,
  })

  return (
    <LexicalComposer initialConfig={{ ...initialConfig, ...customInitialConfig }}>
      <div className='rounded relative border'>
        <ToolbarPlugin />
        <div className='relative'>
          <EditorRefPlugin editorRef={editorRef} />
          <RichTextPlugin
            contentEditable={<ContentEditable className={contentEditableClassnames} />}
            placeholder={<Placeholder />}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <ListPlugin />
          <OnChangePlugin onChange={onChange} />
          <HistoryPlugin />
          <BeautifulMentionsPlugin
            items={mentions?.items}
            menuComponent={MentionsMenu}
            menuItemComponent={MentionsMenuItem}
          />

          {debugMode && <TreeViewPlugin />}
        </div>
      </div>
    </LexicalComposer>
  )
}
